import { CustomerRepository, CustomerFilters } from './CustomerRepository';
import { Customer, CustomerPersistenceData } from '../entities/Customer';
import { supabase } from '@cleanpro/common-ui';

// Database row type from Supabase
interface CustomerDatabaseRow {
  id: string;
  name: string;
  email: string;
  phone: string;
  notes: string | null;
  rating: number | null;
  total_spent: number | null;
  last_service: string | null;
  created_at: string | null;
  updated_at: string | null;
  user_id: string | null;
}

// Helper to transform database row to CustomerPersistenceData
const transformToPersistenceData = (row: CustomerDatabaseRow): CustomerPersistenceData => ({
  id: row.id,
  name: row.name,
  email: row.email,
  phone: row.phone,
  address: row.notes || '', // Use notes as address since database doesn't have address column
  preferences: row.notes || undefined,
  rating: row.rating || undefined,
  total_spent: row.total_spent || undefined,
  last_service: row.last_service || undefined,
  created_at: row.created_at || new Date().toISOString(),
  updated_at: row.updated_at || new Date().toISOString()
});

export class SupabaseCustomerRepository implements CustomerRepository {
  async findById(id: string): Promise<Customer | null> {
    const { data, error } = await supabase
      .from('customers')
      .select('*')
      .eq('id', id)
      .single();

    if (error) {
      if (error.code === 'PGRST116') return null; // Not found
      throw error;
    }

    return Customer.fromPersistence(transformToPersistenceData(data));
  }

  async findByEmail(email: string): Promise<Customer | null> {
    const { data, error } = await supabase
      .from('customers')
      .select('*')
      .eq('email', email)
      .single();

    if (error) {
      if (error.code === 'PGRST116') return null; // Not found
      throw error;
    }

    return Customer.fromPersistence(transformToPersistenceData(data));
  }

  async findAll(): Promise<Customer[]> {
    const { data, error } = await supabase
      .from('customers')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) throw error;

    return data?.map(row => Customer.fromPersistence(transformToPersistenceData(row))) || [];
  }

  async save(customer: Customer): Promise<Customer> {
    const persistenceData = customer.toPersistence();
    
    // Check if customer exists (has a real ID from database)
    const { data: existing } = await supabase
      .from('customers')
      .select('id')
      .eq('id', customer.id)
      .single();

    if (existing) {
      // Update existing customer
      const { data, error } = await supabase
        .from('customers')
        .update(persistenceData)
        .eq('id', customer.id)
        .select()
        .single();

      if (error) throw error;
      return Customer.fromPersistence(transformToPersistenceData(data));
    } else {
      // Create new customer
      const { data, error } = await supabase
        .from('customers')
        .insert([persistenceData])
        .select()
        .single();

      if (error) throw error;
      return Customer.fromPersistence(transformToPersistenceData(data));
    }
  }

  async delete(id: string): Promise<void> {
    const { error } = await supabase
      .from('customers')
      .delete()
      .eq('id', id);

    if (error) throw error;
  }

  async findByFilters(filters: CustomerFilters): Promise<Customer[]> {
    let query = supabase.from('customers').select('*');

    if (filters.search) {
      query = query.or(`name.ilike.%${filters.search}%,email.ilike.%${filters.search}%`);
    }

    if (filters.minTotalSpent !== undefined) {
      query = query.gte('total_spent', filters.minTotalSpent);
    }

    if (filters.maxTotalSpent !== undefined) {
      query = query.lte('total_spent', filters.maxTotalSpent);
    }

    if (filters.minRating !== undefined) {
      query = query.gte('rating', filters.minRating);
    }

    if (filters.maxRating !== undefined) {
      query = query.lte('rating', filters.maxRating);
    }

    const { data, error } = await query.order('created_at', { ascending: false });

    if (error) throw error;

    return data?.map(row => Customer.fromPersistence(transformToPersistenceData(row))) || [];
  }

  async count(): Promise<number> {
    const { count, error } = await supabase
      .from('customers')
      .select('*', { count: 'exact', head: true });

    if (error) throw error;

    return count || 0;
  }
}